// root 私有数据管理

import type { IAnyObject } from '@yimoka/shared';
import { isSuccess } from '@yimoka/shared';
import { rootStore } from '@yimoka/store';

import { httpPost } from './http';

const dataKey = {
  language: 'language',
  loadingLang: 'loadingLang',
  platform: 'platform',
  tenantID: 'tenantID',
  clientID: 'clientID',
  userID: 'userID',
  studentID: 'studentID',
  userToken: 'userToken',
  studentToken: 'studentToken',
  tenant: 'tenant',
};

const getStudentTokenKey = (tenantID: string) => `${tenantID}:${dataKey.studentToken}`;
const getStudentKey = (tenantID: string) => `${tenantID}:${dataKey.studentID}`;

/** 平台类型：h5 移动端 / pc 桌面端 */
export type Platform = 'h5' | 'pc';

/** 平台常量 */
export const PLATFORM_H5 = 'h5';
export const PLATFORM_PC = 'pc';

/** H5 判定断点宽度（px），小于此宽度视为移动端 */
export const PLATFORM_BREAKPOINT = 768;

/** 根据 UA 和窗口宽度检测是否为 H5 环境 */
const detectIsH5 = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isNarrowScreen = window.innerWidth < PLATFORM_BREAKPOINT;
  return isMobileUA || isNarrowScreen;
};

export const setPlatform = (platform: Platform) => {
  localStorage.setItem(dataKey.platform, platform);
  rootStore.setDataItem(dataKey.platform, platform);
};

export const getPlatform = (): Platform => {
  const platform = rootStore.getDataItem(dataKey.platform) as Platform | undefined;
  if (platform === PLATFORM_H5 || platform === PLATFORM_PC) return platform;
  const localPlatform = localStorage.getItem(dataKey.platform) as Platform | null;
  if (localPlatform === PLATFORM_H5 || localPlatform === PLATFORM_PC) {
    setPlatform(localPlatform);
    return localPlatform;
  }
  return detectIsH5() ? PLATFORM_H5 : PLATFORM_PC;
};

export const usePlatform = () => getPlatform();

export const setLanguage = (language: string) => {
  localStorage.setItem(dataKey.language, language);
  rootStore.setDataItem(dataKey.language, language);
  languageListeners.forEach(listener => listener());
};

export const getI18nLang = (lang: string) => (lang ?? DEFAULT_LANGUAGE).split('-')[0];

// 支持的语言
export const SUPPORT_LANGUAGES = [{ label: '中文', value: 'zh-CN', path: 'zh' }, { label: 'EN', value: 'en-US', path: 'en' }];
// 默认的语文
export const DEFAULT_LANGUAGE = 'zh-CN';
const languageListeners = new Set<() => void>();

export const getLanguage = (): string => {
  const language = rootStore.getDataItem(dataKey.language);
  if (language) {
    return language;
  }
  const lang = localStorage.getItem(dataKey.language);
  if (lang) {
    setLanguage(lang);
    return lang;
  }
  // 无存储偏好时默认中文，用户点击 English 后切换为英文
  return DEFAULT_LANGUAGE;
};

export const useLanguage = () => getLanguage();

export const subscribeLanguage = (listener: () => void): (() => void) => {
  languageListeners.add(listener);
  return () => languageListeners.delete(listener);
};

export const setLoadingLang = (loading: boolean) => {
  rootStore.setDataItem(dataKey.loadingLang, loading);
};

export const getLoadingLang = () => rootStore.getDataItem(dataKey.loadingLang) ?? false;

export const useLoadingLang = () => getLoadingLang();

export const setTenantID = (tenantID: string) => {
  try {
    localStorage.setItem(dataKey.tenantID, tenantID);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.tenantID, tenantID);
};

export const getTenantID = (): string | null => {
  const tenant = rootStore.getDataItem(dataKey.tenantID);
  if (tenant) {
    return String(tenant);
  }
  try {
    const local = localStorage.getItem(dataKey.tenantID);
    if (local) {
      rootStore.setDataItem(dataKey.tenantID, local);
      return local;
    }
  } catch {
    // ignore
  }
  return null;
};

export const useTenantID = () => getTenantID();

export const removeTenantID = () => {
  try {
    localStorage.removeItem(dataKey.tenantID);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.tenantID, '');
};

export const setClientID = (clientID: string) => {
  localStorage.setItem(dataKey.clientID, clientID);
  rootStore.setDataItem(dataKey.clientID, clientID);
};

export const getClientID = () => {
  const clientID = rootStore.getDataItem(dataKey.clientID);
  if (clientID) {
    return clientID;
  }
  const id = localStorage.getItem(dataKey.clientID);
  if (id) {
    setClientID(id);
    return id;
  }
  getFetchClientID().then(id => setClientID(id));
  return '';
};

export const useClientID = () => getClientID();

let fetchClientID: Promise<string> | null = null;
const getFetchClientID = () => {
  if (!fetchClientID) {
    fetchClientID = new Promise<string>((resolve) => {
      httpPost('/base/user/portal/id/create').then(res => resolve(isSuccess(res) ? String(res.data ?? '') : ''));
    });
  }
  return fetchClientID;
};

export const setUserID = (userID: string) => {
  localStorage.setItem(dataKey.userID, userID);
  rootStore.setDataItem(dataKey.userID, userID);
};

export const getUserID = () => {
  const uid = rootStore.getDataItem(dataKey.userID);
  if (uid) {
    return String(uid);
  }
  const local = localStorage.getItem(dataKey.userID);
  if (local) {
    setUserID(local);
    return local;
  }
  return '';
};

export const useUserID = () => getUserID();

export const clearUserID = () => {
  try {
    localStorage.removeItem(dataKey.userID);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.userID, '');
};

export const setStudentID = (studentID: string) => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return '';
  }
  const key = getStudentKey(tenantID);
  rootStore.setDataItem(key, studentID);
  rootStore.setDataItem(dataKey.studentID, studentID);
  try {
    localStorage.setItem(key, studentID);
  } catch {
    // ignore
  }
};

export const getStudentID = () => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return '';
  }
  const key = getStudentKey(tenantID);
  const sid = rootStore.getDataItem(key);
  if (sid) {
    return String(sid);
  }
  try {
    const local = localStorage.getItem(key);
    if (local) {
      setStudentID(local);
      return local;
    }
  } catch {
    // ignore
  }
  return '';
};

export const useStudentID = () => getStudentID();

export const clearStudentID = () => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return;
  }
  const key = getStudentKey(tenantID);
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
  rootStore.setDataItem(key, '');
  rootStore.setDataItem(dataKey.studentID, '');
};

export const setUserToken = (userToken: string) => {
  localStorage.setItem(dataKey.userToken, userToken);
  rootStore.setDataItem(dataKey.userToken, userToken);
};

export const getUserToken = () => {
  const token = rootStore.getDataItem(dataKey.userToken);
  if (token) {
    return String(token);
  }
  const local = localStorage.getItem(dataKey.userToken);
  if (local) {
    setUserToken(local);
    return local;
  }
  return '';
};

export const useUserToken = () => getUserToken();

export const clearUserToken = () => {
  try {
    localStorage.removeItem(dataKey.userToken);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.userToken, '');
};

export const getStudentToken = () => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return '';
  }
  const key = getStudentTokenKey(tenantID);
  try {
    const studentToken = localStorage.getItem(key);
    if (studentToken) {
      setStudentToken(studentToken);
      return studentToken;
    }
  } catch {
    // ignore
  }
  return '';
};

export const setStudentToken = (studentToken: string) => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return '';
  }
  const key = getStudentTokenKey(tenantID);
  try {
    localStorage.setItem(key, studentToken);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.studentToken, studentToken);
};

export const useStudentToken = () => getStudentToken();

export const clearStudentToken = () => {
  const tenantID = getTenantID();
  if (!tenantID) {
    return;
  }
  const key = getStudentTokenKey(tenantID);
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
  rootStore.setDataItem(dataKey.studentToken, '');
};

export const useTenant = () => rootStore.getDataItem(dataKey.tenant) as ITenant | undefined;

export const getTenant = () => rootStore.getDataItem(dataKey.tenant) as ITenant | undefined;

export interface ITenant {
  id: string;
  name: string;
  logo: string;
  company: string;
  address: string;
  contact: string;
  kind: string;
  country: string;
  province: string;
  city: string;
  labelIDs: string[];
  isVerified: boolean;
  nameLocal: string;
  companyLocal: string;
  createTime: string;
  updateTime: string;
  phonePrefix: string;
  phone: string;
  extra: IAnyObject;
  summary: string;
  extraLocal: IAnyObject;
  labels: string[];
  avatar: string;
  stageIDs: string[];
}

/**
 * 设置当前租户详情（仅内存，与 tenant-web 一致不落 localStorage）。
 *
 * @param data - 租户对象
 */
export const setTenant = (data: ITenant) => {
  rootStore.setDataItem(dataKey.tenant, data);
  if (data?.id) {
    setTenantID(String(data.id));
  }
};

export const getHostName = () => {
  const { hostname } = window.location;
  //  如果是开发模式 将 .local. 替换成 .dev.
  if (import.meta.env.DEV) {
    return hostname.replace('.local.', '.dev.');
  }
  return hostname;
};
